import React, { useEffect, useState } from 'react';
import {
  CheckCircle,
  WarningCircle,
  Clock,
  SpinnerGap,
  XCircle,
  CaretRight,
  X
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import './PaymentStatusModal.css';

/**
 * PaymentStatusModal Component
 * 
 * Real-time payment status tracking modal
 * Shows progress, updates, and allows manual retry
 */
function PaymentStatusModal({
  isOpen,
  status,
  error,
  isPolling,
  attemptCount,
  onClose,
  onRetry,
  onManualCheck,
  studentName,
  amount,
  reference,
}) {
  const [showDetails, setShowDetails] = useState(false);
  const [autoClosing, setAutoClosing] = useState(false);

  // Auto-close on success after 3 seconds
  useEffect(() => {
    if (status?.status === 'success' && !autoClosing) {
      setAutoClosing(true);
      const timer = setTimeout(() => {
        onClose?.();
        setAutoClosing(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [status?.status, onClose, autoClosing]);

  if (!isOpen) return null;

  const statusConfigs = {
    pending: {
      icon: Clock,
      color: '#f59e0b',
      title: 'Payment Processing',
      description: 'Your payment is being processed. This may take a moment.',
    },
    processing: {
      icon: SpinnerGap,
      color: '#3b82f6',
      title: 'Payment Verifying',
      description: 'Confirming your payment with Paystack...',
    },
    success: {
      icon: CheckCircle,
      color: '#10b981',
      title: 'Payment Successful',
      description: 'Your payment has been processed successfully!',
    },
    failed: {
      icon: XCircle,
      color: '#ef4444',
      title: 'Payment Failed',
      description: 'Your payment could not be processed.',
    },
  };

  const currentStatus = status?.status || 'processing';
  const config = statusConfigs[currentStatus] || statusConfigs.processing;
  const IconComponent = config.icon;

  return (
    <div className="payment-modal-overlay">
      <div className="payment-status-modal">
        {/* Close Button */}
        {currentStatus !== 'processing' && !isPolling && (
          <button
            className="modal-close-btn"
            onClick={onClose}
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        )}

        {/* Status Content */}
        <div className="modal-content">
          {/* Icon Animation */}
          <div className="status-icon-container">
            <IconComponent
              size={64}
              weight="duotone"
              color={config.color}
              className={isPolling && currentStatus === 'processing' ? 'spinning' : ''}
            />
          </div>

          {/* Status Messages */}
          <h2 className="modal-title">{config.title}</h2>
          <p className="modal-description">{config.description}</p>

          {/* Payment Details */}
          {studentName || amount ? (
            <div className="payment-details">
              {studentName && (
                <div className="detail-row">
                  <span className="detail-label">Student:</span>
                  <span className="detail-value">{studentName}</span>
                </div>
              )}
              {amount && (
                <div className="detail-row">
                  <span className="detail-label">Amount:</span>
                  <span className="detail-value">
                    GHS {amount.toLocaleString('en-GH', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              )}
              {reference && (
                <div className="detail-row">
                  <span className="detail-label">Reference:</span>
                  <span className="detail-value reference-code">{reference}</span>
                </div>
              )}
            </div>
          ) : null}

          {/* Error Message */}
          {error && (
            <div className="error-message">
              <WarningCircle size={20} weight="fill" />
              <span>{error}</span>
            </div>
          )}

          {/* Polling Information */}
          {isPolling && (
            <div className="polling-info">
              <p className="polling-text">
                Checking payment status... (Attempt {attemptCount})
              </p>
              <div className="progress-bar">
                <div className="progress-fill"></div>
              </div>
            </div>
          )}

          {/* Details Toggle */}
          {status?.message && (
            <button
              className="details-toggle"
              onClick={() => setShowDetails(!showDetails)}
            >
              <span>Payment Details</span>
              <CaretRight
                size={16}
                style={{
                  transform: showDetails ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}
              />
            </button>
          )}

          {/* Expanded Details */}
          {showDetails && status?.message && (
            <div className="expanded-details">
              <pre>{JSON.stringify(status, null, 2)}</pre>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="modal-actions">
          {isPolling ? (
            <button className="btn btn-secondary" disabled>
              <SpinnerGap size={16} className="spinning" />
              <span>Processing...</span>
            </button>
          ) : currentStatus === 'success' ? (
            <button className="btn btn-success" onClick={onClose}>
              <CheckCircle size={16} />
              <span>Done</span>
            </button>
          ) : currentStatus === 'failed' ? (
            <div className="action-group">
              <button className="btn btn-danger" onClick={onManualCheck}>
                <SpinnerGap size={16} />
                <span>Check Status</span>
              </button>
              <button className="btn btn-primary" onClick={onRetry}>
                <CaretRight size={16} />
                <span>Retry Payment</span>
              </button>
            </div>
          ) : (
            <div className="action-group">
              <button className="btn btn-secondary" onClick={onManualCheck}>
                <SpinnerGap size={16} />
                <span>Check Status</span>
              </button>
              <button className="btn btn-secondary" onClick={onClose}>
                <span>Close</span>
              </button>
            </div>
          )}
        </div>

        {/* Auto-close Indicator */}
        {currentStatus === 'success' && autoClosing && (
          <div className="auto-close-indicator">
            Closing in 3 seconds...
          </div>
        )}
      </div>
    </div>
  );
}

export default PaymentStatusModal;
